<!-- Edit or create a context. Edit mode: change times/project/tags, delete. Create mode: begin-in-past or log-completed. -->
<template>
  <Teleport to="body">
    <div v-if="isOpen" class="modal sp-modal-card is-active">
      <div class="modal-background" @click="close"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
          <button class="delete" @click="close"></button>
        </header>
        <section class="modal-card-body">
          <!-- Project & Tags -->
          <div class="field">
            <label class="label">Project &amp; Tags</label>
            <div class="context-modal__entry">
              <span v-if="parser.committedProject.value" class="tag is-small pill-tag pill-project">
                @{{ parser.committedProject.value.name }}
                <button type="button" class="delete is-small" @click="parser.removeProject()"></button>
              </span>
              <span
                v-for="(tag, index) in parser.committedTags.value"
                :key="tag.name"
                class="tag is-small pill-tag pill-tag-item"
              >
                #{{ tag.name }}
                <button type="button" class="delete is-small" @click="parser.removeTag(index)"></button>
              </span>
              <input
                ref="inputEl"
                type="text"
                v-model="parser.inputValue.value"
                class="context-modal__field"
                placeholder="@project or #tag..."
                @keydown="parser.onKeydown($event)"
                @input="parser.onInput()"
              >
              <AutocompleteDropdown
                :visible="parser.showAutocomplete.value"
                :items="parser.autocompleteItems.value"
                :trigger-char="parser.autocompleteType.value === 'project' ? '@' : '#'"
                :query="parser.currentTokenText.value"
                :show-create="parser.showCreateOption.value"
                :selected-index="parser.selectedAutocompleteIndex.value"
                @select="parser.selectItem($event)"
                @create="parser.createFromToken()"
                @hover="parser.selectedAutocompleteIndex.value = $event"
              />
            </div>
          </div>

          <!-- Times -->
          <div class="columns">
            <div class="column" :class="{ 'is-half': isEdit || mode === 'log-completed' }">
              <TimePicker
                v-model="editStartTime"
                label="Start Time"
                :max-date="editEndTime ? new Date(editEndTime) : new Date()"
                :error="startTimeError"
              />
            </div>
            <div v-if="isEdit || mode === 'log-completed'" class="column">
              <template v-if="isEdit && contextIsRunning">
                <div class="field">
                  <label class="label">End Time</label>
                  <div class="control">
                    <span class="tag is-medium is-warning is-light">Running</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <TimePicker
                  v-model="editEndTime"
                  label="End Time"
                  :max-date="new Date()"
                  :min-date="editStartTime ? new Date(editStartTime) : undefined"
                  :error="endTimeError"
                />
              </template>
            </div>
          </div>

          <div v-if="durationPreview" class="field">
            <label class="label">Duration</label>
            <div class="control">
              <input class="input" type="text" :value="durationPreview" readonly>
            </div>
          </div>

          <div v-if="errorMessage" class="notification p-3 is-danger">
            {{ errorMessage }}
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons">
            <button
              class="button is-primary"
              @click="save"
              :disabled="!canSave || isSaving"
              :class="{ 'is-loading': isSaving }"
            >
              {{ isEdit ? 'Save' : (mode === 'log-completed' ? 'Log Context' : 'Begin Context') }}
            </button>
            <button class="button" @click="close" :disabled="isSaving">Cancel</button>
            <button
              v-if="isEdit"
              class="button is-danger is-outlined"
              @click="handleDelete"
              :disabled="isSaving"
            >
              Delete
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { useOmniboxParser } from '../composables/useOmniboxParser'
import AutocompleteDropdown from './AutocompleteDropdown.vue'
import TimePicker from './TimePicker.vue'
import { formatDuration } from '../utils/time'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  context: { type: Object, default: null },
  mode: { type: String, default: 'begin-in-past' },
  prefill: { type: Object, default: null }, // 'begin-in-past' | 'log-completed'
})

const emit = defineEmits(['close', 'saved', 'deleted'])

const store = useBlocksStore()
const parser = useOmniboxParser(store)
const inputEl = ref(null)

const editStartTime = ref('')
const editEndTime = ref('')
const startTimeError = ref('')
const endTimeError = ref('')
const errorMessage = ref('')
const isSaving = ref(false)

const isEdit = computed(() => !!props.context)
const contextIsRunning = computed(() => props.context?.running)

const modalTitle = computed(() => {
  if (isEdit.value) return 'Edit Context'
  return props.mode === 'log-completed' ? 'Log Past Context' : 'Begin Context in Past'
})

const canSave = computed(() => {
  if (!parser.committedProject.value && !parser.committedTags.value.length) return false
  if (!editStartTime.value) return false
  if (isEdit.value && !contextIsRunning.value && !editEndTime.value) return false
  if (props.mode === 'log-completed' && !isEdit.value && !editEndTime.value) return false
  return true
})

const durationPreview = computed(() => {
  if (!editStartTime.value) return ''
  const start = new Date(editStartTime.value)

  if (contextIsRunning.value && isEdit.value) {
    const seconds = Math.floor((new Date() - start) / 1000)
    return formatDuration(seconds)
  }

  if (!editEndTime.value) return ''
  const end = new Date(editEndTime.value)
  if (end <= start) return ''
  return formatDuration(Math.floor((end - start) / 1000))
})

function currentTimeIso() {
  return new Date().toISOString()
}

// Populate form when context prop changes
watch(() => [props.context, props.isOpen], ([ctx, open]) => {
  if (!open) return
  clearErrors()
  isSaving.value = false

  if (ctx) {
    // Edit mode
    editStartTime.value = ctx.started_at || ''
    editEndTime.value = ctx.stopped_at || ''
    parser.reset()
    if (ctx.project) {
      parser.committedProject.value = { id: ctx.project.id, name: ctx.project.name }
    }
    if (ctx.tags?.length) {
      parser.committedTags.value = ctx.tags.map(t => ({ id: t.id, name: t.name }))
    }
  } else {
    // Create mode
    editStartTime.value = props.mode === 'begin-in-past' ? currentTimeIso() : ''
    editEndTime.value = ''
    parser.reset()
    // Pre-fill from history item if provided
    if (props.prefill) {
      if (props.prefill.project) {
        parser.committedProject.value = { id: props.prefill.project.id, name: props.prefill.project.name }
      }
      if (props.prefill.tags?.length) {
        parser.committedTags.value = props.prefill.tags.map(t => ({ id: t.id, name: t.name }))
      }
    }
  }
}, { immediate: true })

watch(editStartTime, (val) => {
  if (val && !editEndTime.value && props.mode === 'log-completed' && !isEdit.value) {
    const now = new Date()
    const d = new Date(val)
    d.setMinutes(d.getMinutes() + 10)
    editEndTime.value = d <= now ? d.toISOString() : now.toISOString()
  }
})

watch(editEndTime, (val) => {
  if (val && !editStartTime.value && props.mode === 'log-completed' && !isEdit.value) {
    const d = new Date(val)
    d.setMinutes(d.getMinutes() - 10)
    editStartTime.value = d.toISOString()
  }
})

function clearErrors() {
  startTimeError.value = ''
  endTimeError.value = ''
  errorMessage.value = ''
}

async function save() {
  clearErrors()

  if (!editStartTime.value) {
    startTimeError.value = 'Start time is required'
    return
  }

  const startTime = new Date(editStartTime.value)
  const now = new Date()

  if (startTime > now) {
    startTimeError.value = 'Start time cannot be in the future'
    return
  }

  if (editEndTime.value) {
    const endTime = new Date(editEndTime.value)
    if (endTime > now) {
      endTimeError.value = 'End time cannot be in the future'
      return
    }
    if (endTime <= startTime) {
      endTimeError.value = 'End time must be after start time'
      return
    }
  }

  parser.finalizeActiveToken()
  await nextTick()

  if (!parser.committedProject.value && !parser.committedTags.value.length) {
    errorMessage.value = 'At least one project or tag is required'
    return
  }

  const payload = parser.getPayload()
  isSaving.value = true

  try {
    if (isEdit.value) {
      const updatePayload = {
        startedAt: editStartTime.value,
        stoppedAt: contextIsRunning.value ? undefined : (editEndTime.value || undefined),
        tagIds: payload.tagIds || [],
        tagNames: payload.tagNames || [],
      }

      if (payload.projectName) {
        updatePayload.projectName = payload.projectName
      } else {
        updatePayload.projectId = payload.projectId || null
      }

      await store.updateContext(props.context.id, {
        ...updatePayload,
      })
    } else {
      await store.startContext({
        projectId: payload.projectId,
        projectName: payload.projectName,
        tagIds: payload.tagIds,
        tagNames: payload.tagNames,
        startedAt: editStartTime.value,
        stoppedAt: editEndTime.value || undefined,
      })
    }
    emit('saved')
    emit('close')
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'An error occurred'
  } finally {
    isSaving.value = false
  }
}

function handleDelete() {
  if (!confirm('Delete this context? This cannot be undone.')) return
  isSaving.value = true
  store.deleteContext(props.context.id)
    .then(() => {
      emit('deleted')
      emit('close')
    })
    .catch((error) => {
      errorMessage.value = error.response?.data?.error || 'An error occurred'
    })
    .finally(() => { isSaving.value = false })
}

function close() {
  emit('close')
}
</script>

<style scoped>
.modal-card-body {
  overflow: visible;
}

.context-modal__entry {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  position: relative;
  border: 1px solid var(--bulma-border);
  border-radius: 4px;
  padding: 0.35em 0.5em;
  min-height: 2.25em;
}

.context-modal__field {
  flex: 1 1 8rem;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0.25em 0;
  font-size: 0.85rem;
  color: var(--bulma-text);
}

.context-modal__field::placeholder {
  color: var(--bulma-text-weak);
}

.pill-tag {
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.8rem;
  box-shadow: none;
}

.pill-tag .delete {
  background: rgba(0, 0, 0, 0.28);
}

.pill-project {
  background: var(--bulma-link-light);
  color: var(--bulma-link-dark);
}

.pill-tag-item {
  background: var(--bulma-info-light);
  color: var(--bulma-info-dark);
}

</style>
